import React from 'react';
import {
  unitSymbol,
  resolutionLabel,
  horizonCommitment,
} from '../helpers/marketProfile';

const Heading = ({children}) => (
  <div className="text-[10px] font-semibold uppercase tracking-[0.11em] text-slate-500 dark:text-gray-400">
    {children}
  </div>
);

const Chip = ({children}) => (
  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600 dark:bg-gray-700/60 dark:text-gray-300">
    {children}
  </span>
);

/**
 * The public "Details" panel of a markets directory row: what this market asks
 * you to forecast, and how that forecast is scored. Deliberately short - the
 * dataset ids, revision windows and reward split live in the program docs,
 * which the footer links to.
 */
export default function MarketDetails({resource, programUrl}) {
  const profile = resource.profile || {};
  const horizons = resource.program_summary?.horizons || [];
  const facts = [
    unitSymbol(profile.unit),
    resolutionLabel(profile.resolution_minutes),
    profile.timezone,
  ].filter(Boolean);

  return (
    <div
      className="mb-6 border-l border-slate-900/10 pb-1 pl-5 dark:border-gray-100/10"
      data-testid="market-details"
    >
      <div className="grid gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(0,9fr)] lg:gap-10">
        <div className="min-w-0">
          <Heading>What you forecast</Heading>
          <p className="mt-2.5 max-w-[46ch] text-sm leading-relaxed text-slate-900 dark:text-white">
            {profile.target_summary}
          </p>
          {facts.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {facts.map(f => <Chip key={f}>{f}</Chip>)}
            </div>
          )}
          {profile.source_name && (
            <p className="mt-4 max-w-[52ch] text-[13px] leading-relaxed text-slate-700 dark:text-gray-300">
              Scored against measurements published by{' '}
              <span className="font-semibold text-slate-900 dark:text-white">
                {profile.source_name}
              </span>
              , a public dataset you can also train on.
              {profile.source_url && (
                <>
                  {' '}
                  <a
                    href={profile.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline underline-offset-4 dark:text-brand-400"
                  >
                    {profile.source_name} ↗
                  </a>
                </>
              )}
            </p>
          )}
          {profile.target_note && (
            <p className="mt-2.5 max-w-[52ch] text-[13px] leading-relaxed text-slate-500 dark:text-gray-400">
              {profile.target_note}
            </p>
          )}
        </div>

        <div className="min-w-0">
          <Heading>What you submit</Heading>
          {horizons.length > 0 ? (
            <>
              <ul className="mt-2.5 flex flex-col gap-2">
                {horizons.map((h) => {
                  const {label, detail} = horizonCommitment(h);
                  return (
                    <li key={h.label} className="flex gap-2.5 text-[13px]">
                      <span className="shrink-0 font-semibold text-slate-900 dark:text-white">
                        {label}
                      </span>
                      <span className="text-slate-500 dark:text-gray-400">{detail}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-[13px] text-slate-700 dark:text-gray-300">
                Three quantiles per timestamp:{' '}
                <span className="font-semibold text-slate-900 dark:text-white">Q10, Q50, Q90</span>.
              </p>

              <div className="my-4 h-px bg-slate-900/[0.06] dark:bg-gray-100/[0.07]"/>

              <Heading>How it is scored</Heading>
              <ul className="mt-2.5 flex flex-col gap-2">
                <li className="flex gap-2.5 text-[13px]">
                  <span className="w-[86px] shrink-0 font-semibold text-slate-900 dark:text-white">
                    Deterministic
                  </span>
                  <span className="text-slate-500 dark:text-gray-400">
                    your Q50 against the measurement, as{' '}
                    <span className="font-semibold text-slate-900 dark:text-white">RMSE</span>
                  </span>
                </li>
                <li className="flex gap-2.5 text-[13px]">
                  <span className="w-[86px] shrink-0 font-semibold text-slate-900 dark:text-white">
                    Probabilistic
                  </span>
                  <span className="text-slate-500 dark:text-gray-400">
                    your Q10–Q90 interval, as{' '}
                    <span className="font-semibold text-slate-900 dark:text-white">MWI</span>:
                    narrow intervals score better, but only while they keep covering
                    the outcome
                  </span>
                </li>
              </ul>
              <p className="mt-3 max-w-[52ch] text-[13px] leading-relaxed text-slate-700 dark:text-gray-300">
                Both tracks are scored every day, for each horizon separately, and
                ranked against the other forecasters in this market.
              </p>
            </>
          ) : (
            <p className="mt-2.5 max-w-[52ch] text-[13px] leading-relaxed text-slate-500 dark:text-gray-400">
              No paid challenges are published for this market yet, so there is
              nothing to submit against it today.
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 border-t border-slate-900/[0.06] pt-3.5 dark:border-gray-100/[0.07]">
            {programUrl ? (
              <a
                href={programUrl}
                className="text-xs text-brand-600 hover:underline underline-offset-4 dark:text-brand-400"
              >
                Full target spec, datasets and reward rules ↗
              </a>
            ) : (
              <span className="text-xs text-slate-400 dark:text-gray-500">
                Program page in preparation
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
